import { FC, ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAppSelector } from "../../hooks/redux";
import { routes } from "./routes";
import { isHomeHealthAtRiskOnlyUser } from "../../util/atRiskOverrideHelpers";

interface HomeHealthRouteGuardProps {
  children: ReactNode;
}

export const HomeHealthRouteGuard: FC<HomeHealthRouteGuardProps> = ({
  children,
}) => {
  const location = useLocation();
  const { user } = useAppSelector((state) => state.auth);
  const { patients } = useAppSelector((state) => state.homehealth);
  const { userClinic } = useAppSelector((state) => state.clinic);

  // VRN home-health restricted view: only the Scheduling, Insights, Vitals
  // AI, IQ/Neo, and Medications modules are hidden for this role; the rest
  // of the side navigation (Inbox, Clinical, Watchlist, Consents & devices,
  // other AI Agents, patient profile, Navigator, settings, ...) remains
  // accessible. See documentation/VRN_HOMEHEALTH_AT_RISK_REVIEW.md.
  if (isHomeHealthAtRiskOnlyUser({ user, clinic: userClinic })) {
    const blockedPathPrefixes = [
      routes.npScheduling.path, // "/np-scheduling"
      routes.Insights.path, // "/insights"
      routes.vitalSync.path, // "/vitals-ai"
      routes.iq.path, // "/iq"
      routes.neo.path, // "/neo"
      routes.medRecAI.path, // "/medications"
    ];
    const isBlockedPath = blockedPathPrefixes.some((prefix) =>
      location.pathname.startsWith(prefix)
    );
    if (isBlockedPath) {
      return <Navigate to="/navigator" replace />;
    }
    return <>{children}</>;
  }

  // If user has homeHealthId, they should only be able to access patient profile
  if (user?.homeHealthId) {
    // Allow access to patient profile routes
    if (location.pathname.startsWith("/patient-profile")) {
      return <>{children}</>;
    }

    // Redirect to patient profile for all other routes
    // Use the patientId from Redux if available, otherwise fall back to homeHealthId
    const redirectId = patients.find(
      (p: any) => p._id === user.homeHealthId
    )?.patient_id;

    if (redirectId) {
      return (
        <Navigate
          to={routes.patientProfile.path.replace(
            ":patientId",
            String(redirectId)
          )}
          replace
        />
      );
    }
  }

  // For non-home health users, allow access to all routes
  return <>{children}</>;
};
